import { useCallback, useState } from 'react';
import Swal from 'sweetalert2';
import {
  fetchCRDocumentClassByType,
  startConductRiskReport,
  unlinkConductRiskSource,
  uploadConductRiskSource,
} from '../../../../api/functionApi';
import { SOURCE_ID_TO_DOC_TYPE, docTypeAccept, docTypeLabel } from '../../lista-report/sources';
import type {
  ConductRiskDocumentType,
  ConductRiskSourceLink,
} from '../../../../api/apiTypesConductRisk';

/** Fallback dei mimetype quando il browser non li setta. */
const mimeForFile = (file: File): string | undefined => {
  if (file.type) return file.type;
  const ext = file.name.split('.').pop()?.toLowerCase();
  return ext === 'html' ? 'text/html'
       : ext === 'htm'  ? 'text/html'
       : ext === 'xlsx' ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
       : ext === 'xls'  ? 'application/vnd.ms-excel'
       : ext === 'xlsb' ? 'application/vnd.ms-excel.sheet.binary.macroEnabled.12'
       : ext === 'pdf'  ? 'application/pdf'
       : undefined;
};

interface UseSourcesActionsParams {
  reportId:           string;
  sourceLinks:        ConductRiskSourceLink[];
  refetchSources:     () => Promise<void>;
  refetchValidation:  () => Promise<void>;
}

interface UseSourcesActionsResult {
  replacingDocType: string | null;
  handleReplaceFile: (docType: ConductRiskDocumentType) => void;
  handleUnlink:      (docType: ConductRiskDocumentType) => Promise<void>;
  handleAddSources:  (data: { files: Record<string, File[]> }) => Promise<void>;
}

/**
 * Raccoglie tutte le azioni async sulle fonti CR: upload di singole fonti,
 * sostituzione per errori parser (DELETE + upload + start), unlink di fonti
 * gia' processate, e caricamento multiplo dal modal "Carica Fonti".
 */
export const useSourcesActions = ({
  reportId,
  sourceLinks,
  refetchSources,
  refetchValidation,
}: UseSourcesActionsParams): UseSourcesActionsResult => {
  const [replacingDocType, setReplacingDocType] = useState<string | null>(null);

  const uploadForDocType = useCallback(async (docType: string, file: File) => {
    const dc = await fetchCRDocumentClassByType(docType);
    await uploadConductRiskSource(reportId, file, dc.id, mimeForFile(file));
  }, [reportId]);

  const handleReplaceFile = useCallback((docType: ConductRiskDocumentType) => {
    if (!reportId || replacingDocType) return;
    const inputEl = document.createElement('input');
    inputEl.type = 'file';
    inputEl.accept = docTypeAccept(docType);
    inputEl.onchange = async () => {
      const file = inputEl.files?.[0];
      if (!file) return;
      setReplacingDocType(docType);
      try {
        // Scollega prima il link esistente (se c'e') per evitare conflitti unique
        // (report_id, document_class_id).
        const existing = sourceLinks.find(l => l.document_type === docType);
        if (existing) await unlinkConductRiskSource(reportId, existing.id);
        await uploadForDocType(docType, file);
        await startConductRiskReport(reportId);
        await refetchSources();
        await refetchValidation();
        Swal.fire({ icon: 'success', title: 'File sostituito', text: 'Parsing riavviato.' });
      } catch (err: any) {
        console.error('[CR replace] fallito per', docType, err);
        Swal.fire({ icon: 'error', title: 'Sostituzione fallita', text: err?.message ?? 'Riprova.' });
      } finally {
        setReplacingDocType(null);
      }
    };
    inputEl.click();
  }, [reportId, replacingDocType, sourceLinks, uploadForDocType, refetchSources, refetchValidation]);

  const handleUnlink = useCallback(async (docType: ConductRiskDocumentType) => {
    const link = sourceLinks.find(l => l.document_type === docType);
    if (!link) return;
    const label = link.source?.file_name ?? docTypeLabel(docType);
    const confirm = await Swal.fire({
      icon: 'warning',
      title: 'Rimuovere la fonte?',
      text: `La fonte "${label}" verrà scollegata.`,
      showCancelButton: true,
      confirmButtonText: 'Rimuovi',
      cancelButtonText: 'Annulla',
      confirmButtonColor: '#dc2626',
    });
    if (!confirm.isConfirmed) return;
    try {
      await unlinkConductRiskSource(reportId, link.id);
      await refetchSources();
      await refetchValidation();
    } catch (err: any) {
      Swal.fire({ icon: 'error', title: 'Errore', text: err?.message ?? 'Rimozione fallita.' });
    }
  }, [reportId, sourceLinks, refetchSources, refetchValidation]);

  const handleAddSources = useCallback(async (data: { files: Record<string, File[]> }) => {
    if (!reportId) return;
    const failed: string[] = [];
    const uploadJobs: Promise<unknown>[] = [];
    Object.entries(data.files).forEach(([sourceId, fileList]) => {
      const docType = SOURCE_ID_TO_DOC_TYPE[sourceId];
      if (!docType) {
        console.warn(`[CR upload] nessun document_type mappato per "${sourceId}", skip.`);
        return;
      }
      fileList.forEach((file) => {
        uploadJobs.push(
          uploadForDocType(docType, file).catch((err) => {
            console.error(`[CR upload] fallito per ${sourceId}/${file.name}:`, err);
            failed.push(`${sourceId}: ${file.name}`);
          }),
        );
      });
    });
    if (uploadJobs.length > 0) await Promise.all(uploadJobs);

    await refetchSources();

    if (failed.length > 0) {
      Swal.fire({
        icon: 'warning',
        title: 'Alcuni upload sono falliti',
        text: `File non caricati: ${failed.join(', ')}`,
      });
      return;
    }

    // Rilancia i DAG per i driver che dipendono dalle nuove fonti.
    try {
      await startConductRiskReport(reportId);
    } catch (err: any) {
      console.warn('[CR] start non disponibile:', err);
    }
    await refetchValidation();
  }, [reportId, uploadForDocType, refetchSources, refetchValidation]);

  return { replacingDocType, handleReplaceFile, handleUnlink, handleAddSources };
};
